import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { FileBeforeUploadEvent, FileUploadEvent } from 'primeng/fileupload';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SelectableDocument } from './file-upload.service';
import { ExtractionConfig } from '@shared/index';
import { DocumentChunk } from '@reflact/docsearch';

export interface ProjectDocumentsPageQuery {
  offset: number;
  limit: number;
  sortField: 'filename' | 'uploadDate';
  sortOrder: 1 | -1;
  search: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectDocumentsService {
  private _selectedProjectId: string = "";
  public uploadEndpoint = "/api/toolbox/bot/admin/botdoc/upload";
  public replaceEndpoint = "/api/toolbox/bot/admin/botdoc/replace";

  public projectDocuments: BehaviorSubject<SelectableDocument[]> = new BehaviorSubject<SelectableDocument[]>([]);
  public readonly totalRecords = signal(0);
  public readonly first = signal(0);
  public readonly pageSize = signal(25);
  public readonly loading = signal(false);
  public readonly loadFailed = signal(false);
  private readonly toastr = inject(ToastrService);
  private requestId = 0;
  private pageQuery: ProjectDocumentsPageQuery = {
    offset: 0, limit: 25, sortField: 'uploadDate', sortOrder: -1, search: ''
  };

  constructor(public http: HttpClient) {

  }
  public addFormFieldsForReplace(event: FileBeforeUploadEvent, docId: string) {
    event.formData.append('docid', docId);
  }
  public onReplaceDone(_event: FileUploadEvent) {
    void this.updateProjectDocuments();
  }



  public addFormFieldsForUpload(event: FileBeforeUploadEvent) {

    event.formData.append('projectId', this._selectedProjectId);
  }
  public fileUploadDone(_event: FileUploadEvent) {
    void this.updateProjectDocuments();
  }


  private async updateProjectDocuments() {
    const requestId = ++this.requestId;
    this.loading.set(true);
    this.loadFailed.set(false);
    try {
      const result = await firstValueFrom(this.http.get<{ docs: SelectableDocument[]; total: number }>(
        "/api/toolbox/bot/admin/projectdoc/files/" + (this._selectedProjectId || '-'),
        { params: { ...this.pageQuery } }
      ));
      if (requestId !== this.requestId) return;
      this.totalRecords.set(result.total);
      const lastPageOffset = Math.max(0, Math.ceil(result.total / this.pageQuery.limit) - 1) * this.pageQuery.limit;
      if (this.pageQuery.offset > lastPageOffset) {
        this.pageQuery = { ...this.pageQuery, offset: lastPageOffset };
        this.first.set(lastPageOffset);
        await this.updateProjectDocuments();
        return;
      }
      this.projectDocuments.next(result.docs);
    } catch (error) {
      if (requestId !== this.requestId) return;
      console.error('Error while loading project documents', error);
      this.projectDocuments.next([]);
      this.loadFailed.set(true);
      this.toastr.error('LOADED');
    } finally {
      if (requestId === this.requestId) this.loading.set(false);
    }
  }

  public async loadPage(query: ProjectDocumentsPageQuery) {
    this.pageQuery = query;
    this.first.set(query.offset);
    this.pageSize.set(query.limit);
    await this.updateProjectDocuments();
  }

  public async reloadDocuments() {
    await this.updateProjectDocuments();
  }


  public async deleteFile(fileId: string, reload = true) {
    try {
      const res = await firstValueFrom(this.http.delete(`/api/toolbox/bot/admin/botdoc/delete/${this.selectedProjectId}/${fileId}`));
      if (reload) await this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while deleting file', error);
      return false;
    }
  }

  public async setDocumentAutoUpdate(fileId: string, autoUpdateEnabled: boolean) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/autoupdate/${this.selectedProjectId}/${fileId}`,
        { autoUpdateEnabled }
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while updating auto update flag', error);
      return false;
    }
  }

  public async reloadWebsiteDocument(fileId: string) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/${this.selectedProjectId}/${fileId}/website-reload`,
        {}
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while reloading website document', error);
      return false;
    }
  }

  public async updateWebsiteAutoUpdateConfig(
    fileId: string,
    autoUpdateEnabled: boolean,
    autoUpdateIntervalDays?: number,
    urls?: string[]
  ) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/${this.selectedProjectId}/${fileId}/website-update-config`,
        { autoUpdateEnabled, autoUpdateIntervalDays, urls }
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while updating website auto-update config', error);
      return false;
    }
  }

  public async triggerExtraction(docId: string, config: ExtractionConfig): Promise<{ status: string, chunkCount?: number, message?: string, errorType?: string } | null> {
    try {
      const res = await firstValueFrom(this.http.post<{ status: string, chunkCount?: number, message?: string, errorType?: string }>(
        `/api/toolbox/bot/admin/botdoc/extract/${docId}`,
        config
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error: unknown) {
      console.error('Error while triggering extraction', error);
      const e = error as { error?: { message?: string; errorType?: string } };
      const message: string | undefined = e.error?.message ?? (error as { message?: string }).message;
      const errorType: string | undefined = e.error?.errorType;
      return { status: "error", message, errorType };
    }
  }

  public set selectedProjectId(id: string) {
    if (id != this._selectedProjectId) {
      this._selectedProjectId = id;
      ++this.requestId;
      this.pageQuery = { offset: 0, limit: 25, sortField: 'uploadDate', sortOrder: -1, search: '' };
      this.first.set(0);
      this.pageSize.set(25);
      this.totalRecords.set(0);
      this.loading.set(false);
      this.loadFailed.set(false);
      this.projectDocuments.next([]);
    }

  }

  public get selectedProjectId() {
    return this._selectedProjectId;
  }

  public async getProjectDocChunks(docSearchId: string): Promise<DocumentChunk[]> {
    return firstValueFrom(this.http.get<DocumentChunk[]>(`/api/toolbox/bot/admin/projectdoc/chunks/${docSearchId}`));
  }
}
